import { compact, deburr } from 'lodash';
import {
  PDFDocument,
  PDFFont,
  PDFPage,
  PageSizes,
  StandardFonts,
  rgb,
} from 'pdf-lib';
import {
  ExactMedicalHistoryEntry,
  ExactMedicalHistoryEntryKind,
} from './medical-history-form';

export interface IExactMedicalHistoryPdfData {
  patientName: string;
  historyDate: string;
  recordedBy?: string;
  entries: ExactMedicalHistoryEntry[];
}

const [pageWidth, pageHeight] = PageSizes.A4;
const pageMargin = 50;
const titleFontSize = 18;
const sectionFontSize = 12;
const bodyFontSize = 10;
const bodyLineHeight = 14;
const sectionSpacing = 10;
const answerColumnWidth = 32;
const valueIndent = 12;
const inkColour = rgb(0.1, 0.1, 0.1);
const ruleColour = rgb(0.75, 0.75, 0.75);

const unencodableCharacterReplacement = '?';
const whitespaceRun = /\s+/;

export class ExactMedicalHistoryPdf {
  static async render(data: IExactMedicalHistoryPdfData): Promise<Uint8Array> {
    const document = await PDFDocument.create();
    const regular = await document.embedFont(StandardFonts.Helvetica);
    const bold = await document.embedFont(StandardFonts.HelveticaBold);
    const writer = new MedicalHistoryPdfWriter(document, regular, bold);

    writer.drawTitle('Medical History');
    compact([
      data.patientName,
      `Recorded ${data.historyDate}`,
      data.recordedBy ? `Recorded by ${data.recordedBy}` : undefined,
    ]).forEach((line) => writer.drawSubtitle(line));
    writer.drawRule();

    data.entries.forEach((entry) => writer.drawEntry(entry));

    return document.save();
  }
}

class MedicalHistoryPdfWriter {
  private _page: PDFPage;
  private _cursorY = pageHeight - pageMargin;

  constructor(
    private _document: PDFDocument,
    private _regular: PDFFont,
    private _bold: PDFFont
  ) {
    this._page = this._document.addPage(PageSizes.A4);
  }

  /**
   * pdf-lib's standard fonts throw on anything outside WinAnsi. Accents are
   * stripped rather than dropped so names stay recognisable.
   */
  private static _toEncodable(text: string, font: PDFFont): string {
    if (MedicalHistoryPdfWriter._canRender(text, font)) {
      return text;
    }

    return Array.from(text)
      .map((character) => {
        if (MedicalHistoryPdfWriter._canRender(character, font)) {
          return character;
        }
        const deburred = deburr(character);
        return MedicalHistoryPdfWriter._canRender(deburred, font)
          ? deburred
          : unencodableCharacterReplacement;
      })
      .join('');
  }

  private static _canRender(text: string, font: PDFFont): boolean {
    try {
      font.encodeText(text);
      return true;
    } catch {
      return false;
    }
  }

  drawTitle(title: string): void {
    this._drawLines([title], this._bold, titleFontSize, pageMargin);
    this._advance(sectionSpacing / 2);
  }

  drawSubtitle(line: string): void {
    this._drawLines([line], this._regular, bodyFontSize, pageMargin);
  }

  drawRule(): void {
    this._advance(sectionSpacing / 2);
    this._claimSpace(1, 0);
    this._page.drawLine({
      start: { x: pageMargin, y: this._cursorY },
      end: { x: pageWidth - pageMargin, y: this._cursorY },
      thickness: 0.5,
      color: ruleColour,
    });
    this._advance(sectionSpacing);
  }

  drawEntry(entry: ExactMedicalHistoryEntry): void {
    switch (entry.kind) {
      case ExactMedicalHistoryEntryKind.Section:
        return this._drawSection(entry.label);
      case ExactMedicalHistoryEntryKind.Boolean:
        return this._drawBoolean(entry.label, entry.answer);
      case ExactMedicalHistoryEntryKind.Text:
        return this._drawText(entry.label, entry.value);
      default:
        return;
    }
  }

  private _drawSection(label: string): void {
    this._advance(sectionSpacing);
    this._drawLines([label], this._bold, sectionFontSize, pageMargin);
    this._advance(sectionSpacing / 2);
  }

  private _drawBoolean(label: string, answer: boolean): void {
    const answerX = pageWidth - pageMargin - answerColumnWidth;
    const labelLines = this._wrap(
      label,
      this._regular,
      bodyFontSize,
      answerX - pageMargin - valueIndent
    );
    const answerBaselineY = this._claimSpace(
      bodyLineHeight * labelLines.length,
      bodyFontSize
    );

    labelLines.forEach((line, index) =>
      this._page.drawText(line, {
        x: pageMargin,
        y: answerBaselineY - bodyLineHeight * index,
        size: bodyFontSize,
        font: this._regular,
        color: inkColour,
      })
    );
    this._page.drawText(answer ? 'Yes' : 'No', {
      x: answerX,
      y: answerBaselineY,
      size: bodyFontSize,
      font: answer ? this._bold : this._regular,
      color: inkColour,
    });
    this._advance(bodyLineHeight * labelLines.length);
  }

  private _drawText(label: string, value: string): void {
    this._drawLines([label], this._bold, bodyFontSize, pageMargin);
    const availableWidth = pageWidth - pageMargin * 2 - valueIndent;
    const lines = value
      .split('\n')
      .flatMap((paragraph) =>
        this._wrap(paragraph, this._regular, bodyFontSize, availableWidth)
      );
    this._drawLines(
      lines,
      this._regular,
      bodyFontSize,
      pageMargin + valueIndent
    );
    this._advance(sectionSpacing / 2);
  }

  private _drawLines(
    lines: string[],
    font: PDFFont,
    size: number,
    x: number
  ): void {
    const lineHeight = Math.max(bodyLineHeight, size * 1.4);

    lines.forEach((line) => {
      const baselineY = this._claimSpace(lineHeight, size);
      this._page.drawText(MedicalHistoryPdfWriter._toEncodable(line, font), {
        x,
        y: baselineY,
        size,
        font,
        color: inkColour,
      });
      this._advance(lineHeight);
    });
  }

  /**
   * Moves to a new page when the requested block does not fit, then returns the
   * baseline to draw the first line at.
   */
  private _claimSpace(height: number, fontSize: number): number {
    if (this._cursorY - height < pageMargin) {
      this._page = this._document.addPage(PageSizes.A4);
      this._cursorY = pageHeight - pageMargin;
    }
    return this._cursorY - fontSize;
  }

  private _advance(height: number): void {
    this._cursorY -= height;
  }

  private _wrap(
    text: string,
    font: PDFFont,
    size: number,
    maxWidth: number
  ): string[] {
    const encodable = MedicalHistoryPdfWriter._toEncodable(text, font);
    const words = encodable.split(whitespaceRun).filter((word) => word.length);
    if (!words.length) {
      return [''];
    }

    return words.reduce<string[]>((lines, word) => {
      const currentLine = lines[lines.length - 1];
      if (currentLine === undefined) {
        return [word];
      }
      const candidate = `${currentLine} ${word}`;
      if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
        return [...lines.slice(0, -1), candidate];
      }
      return [...lines, word];
    }, []);
  }
}
